/**
 * Meta Pixel & Conversions API Tracking Utility
 * Production-grade implementation for SPA (React) sites.
 *
 * Architecture:
 * - ONE pixel for the entire site, loaded dynamically from CMS config
 * - Browser Pixel (fbevents.js) + Server CAPI (/api/meta/track) fired together
 * - Same event_id for deduplication between browser and server
 * - Event queueing: events buffered until CMS config loads, then replayed
 * - Purchase fires ONLY after confirmed payment (sessionStorage dedup)
 * - SPA route changes trigger PageView via both browser + CAPI
 */

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Dynamic Config with Event Queue ───

let _pixelId = null;
let _configLoaded = false;
let _configPromise = null;
let _eventQueue = []; // Buffer events until config loads

/** Fetch Pixel ID from CMS API (cached, fires once) */
function loadMetaConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = fetch(`${API}/api/public/meta-config`)
    .then(r => r.json())
    .then(data => {
      _configLoaded = true;
      if (data.enabled && data.pixel_id) {
        _pixelId = data.pixel_id;
        _initPixel(data.pixel_id);
        _flushQueue();
      } else {
        // Config disabled — flush queue anyway (CAPI calls already went out)
        _eventQueue = [];
      }
    })
    .catch(() => {
      _configLoaded = true;
      _eventQueue = [];
    });
  return _configPromise;
}

/**
 * Initialize Meta Pixel (fbevents.js) once at root level.
 * Only loads the script and calls fbq('init') — does NOT fire PageView.
 * PageView is handled exclusively by RouteChangeTracker in App.js
 * to avoid double-firing on first load.
 */
function _initPixel(pixelId) {
  if (!pixelId || typeof window === 'undefined') return;
  if (typeof window.fbq === 'function') {
    // fbevents.js already loaded — just init with this pixel
    window.fbq('init', pixelId);
    return;
  }
  // Load fbevents.js script
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=true;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=true;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', pixelId);
}

/** Replay queued browser pixel events after config loads */
function _flushQueue() {
  if (!_pixelId || typeof window.fbq !== 'function') return;
  const queue = [..._eventQueue];
  _eventQueue = [];
  queue.forEach(({ eventName, customData, eventId }) => {
    window.fbq('track', eventName, customData, { eventID: eventId });
  });
}

// Auto-load config on module import
loadMetaConfig();

// ─── Helpers ───

function generateEventId(eventName) {
  return `${eventName}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
}

function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : '';
  } catch { return ''; }
}

function getFbCookies() {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return {
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc') || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : ''),
  };
}

function isPixelReady() {
  return typeof window.fbq === 'function' && !!_pixelId;
}

/** Send CAPI event to backend (fire-and-forget, always fires regardless of pixel status) */
async function sendCAPI(eventName, eventId, userData, customData) {
  try {
    const { fbp, fbc } = getFbCookies();
    await fetch(`${API}/api/meta/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        user_data: userData || {},
        custom_data: customData || {},
        fbp,
        fbc,
      }),
    });
  } catch (err) {
    console.warn(`[Meta CAPI] Failed to send ${eventName}:`, err.message);
  }
}

// ─── Purchase Dedup Storage ───

const PURCHASE_STORAGE_KEY = 'meta_purchase_tracked';

function isPurchaseTracked(orderId) {
  try {
    const tracked = JSON.parse(sessionStorage.getItem(PURCHASE_STORAGE_KEY) || '[]');
    return tracked.includes(orderId);
  } catch { return false; }
}

function markPurchaseTracked(orderId) {
  try {
    const tracked = JSON.parse(sessionStorage.getItem(PURCHASE_STORAGE_KEY) || '[]');
    tracked.push(orderId);
    sessionStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(tracked));
  } catch { /* ignore */ }
}

// ─── Core Tracking Function ───

/**
 * Send a Meta event to BOTH browser Pixel and server CAPI.
 * If pixel hasn't loaded yet, the browser event is queued and replayed.
 * CAPI always fires immediately (server-side is independent of pixel load).
 */
export function trackMetaEvent(eventName, { customData = {}, userData = {}, eventId = null } = {}) {
  const eid = eventId || generateEventId(eventName);

  // 1) Browser Pixel — fire or queue
  if (isPixelReady()) {
    window.fbq('track', eventName, customData, { eventID: eid });
  } else if (!_configLoaded) {
    _eventQueue.push({ eventName, customData, eventId: eid });
  }

  // 2) Server CAPI — always fires immediately
  sendCAPI(eventName, eid, userData, customData);
}

// ─── Specific Event Helpers ───

/**
 * PageView — fires on first load AND every SPA route change.
 * Called exclusively by RouteChangeTracker in App.js.
 * Browser: fbq('track', 'PageView')
 * Server: POST /api/meta/track (CAPI) — important for JS app shells
 * where Meta's crawler can't see rendered content.
 */
export function trackPageView() {
  const eid = generateEventId('PageView');

  // Browser Pixel — fire or queue
  if (isPixelReady()) {
    window.fbq('track', 'PageView');
  } else if (!_configLoaded) {
    _eventQueue.push({ eventName: 'PageView', customData: {}, eventId: eid });
  }

  // Server CAPI — always fires
  sendCAPI('PageView', eid, {}, {
    content_name: document.title || '',
    landing_page_path: window.location.pathname,
  });
}

/**
 * ViewContent — fires on event landing pages, product pages.
 * Reads page data from CMS (title, slug) and sends to both Pixel + CAPI.
 * Reusable: works for /suds-1, /suds-2, any /:slug — driven by CMS data.
 */
export function trackViewContent({ slug, title, businessLine = 'Event', value = 0, currency = 'CAD' }) {
  const customData = {
    content_name: title,
    content_category: businessLine,
    content_ids: [slug],
    content_type: 'product',
    value,
    currency,
    landing_page_name: slug,
    landing_page_path: window.location.pathname,
  };
  trackMetaEvent('ViewContent', { customData });
}

/** Lead — contact form submission */
export function trackLead({ name, email, phone, source = 'contact_form' }) {
  const customData = {
    content_name: source,
    content_category: 'lead',
    business_line: 'general',
    landing_page_path: window.location.pathname,
  };
  const userData = { email, phone, first_name: name?.split(' ')[0] || '' };
  trackMetaEvent('Lead', { customData, userData });
}

/** InitiateCheckout — user starts checkout */
export function trackInitiateCheckout({
  slug, title, ticketType, quantity = 1, value = 0, currency = 'CAD', businessLine = 'events'
}) {
  const customData = {
    content_name: title,
    content_category: businessLine,
    content_ids: [slug],
    content_type: 'product',
    value,
    currency,
    num_items: quantity,
    business_line: businessLine,
    event_offer_name: title,
    order_type: ticketType,
    landing_page_name: slug,
    landing_page_path: window.location.pathname,
  };
  trackMetaEvent('InitiateCheckout', { customData });
}

/** AddToCart — for treats/mocktails cart flow */
export function trackAddToCart({ productName, category, value = 0, currency = 'CAD', quantity = 1 }) {
  const customData = {
    content_name: productName,
    content_category: category,
    content_type: 'product',
    value,
    currency,
    num_items: quantity,
    business_line: category,
  };
  trackMetaEvent('AddToCart', { customData });
}

/**
 * Purchase — ONLY after confirmed payment.
 * Protected against duplicate fires via sessionStorage.
 * Uses deterministic event_id: purchase_{orderId} for server dedup.
 */
export function trackPurchase({
  orderId, bookingCode, name, email, phone, title, slug,
  value = 0, currency = 'CAD', quantity = 1, businessLine = 'events', ticketType = ''
}) {
  if (!orderId) return;
  if (isPurchaseTracked(orderId)) {
    return;
  }

  const eventId = `purchase_${orderId}`;
  const customData = {
    value,
    currency,
    content_name: title,
    content_category: businessLine,
    content_ids: [orderId],
    content_type: 'product',
    order_id: orderId,
    booking_code: bookingCode || '',
    num_items: quantity,
    business_line: businessLine,
    event_offer_name: title,
    order_type: ticketType,
    landing_page_name: slug || '',
    landing_page_path: window.location.pathname,
  };
  const userData = {
    email: email || '',
    phone: phone || '',
    first_name: name?.split(' ')[0] || '',
  };

  // Browser pixel with matching event_id
  if (isPixelReady()) {
    window.fbq('track', 'Purchase', customData, { eventID: eventId });
  }

  // Server CAPI with matching event_id
  sendCAPI('Purchase', eventId, userData, customData);

  markPurchaseTracked(orderId);
}
